import { useCallback, useState } from "react";
import ToastService from "../../../services/toast.service";
import logger from "../../../utils/logger";
import { humanizeLabel, formatValue, findScorecardItem } from "../helpers";

const INITIAL_PREVIOUS_MONTH_DIALOG = {
  open: false,
  groupLabel: "",
  itemLabel: "",
  valueLabel: "",
  focus: null,
  reflection: null,
  reflectionNotes: "",
  loading: false,
  error: null,
  month: null,
  year: null,
  readOnly: false,
};

export default function usePreviousMonthDialog({ service, activeDraft, findGoal, isViewingHistory }) {
  const [previousMonthDialog, setPreviousMonthDialog] = useState(INITIAL_PREVIOUS_MONTH_DIALOG);
  const [isReflectionSaving, setIsReflectionSaving] = useState(false);

  const resetPreviousMonthDialog = useCallback(() => {
    setPreviousMonthDialog(INITIAL_PREVIOUS_MONTH_DIALOG);
  }, []);

  const closePreviousMonthDialog = useCallback(() => {
    if (previousMonthDialog.loading || isReflectionSaving) {
      return;
    }
    resetPreviousMonthDialog();
  }, [isReflectionSaving, previousMonthDialog.loading, resetPreviousMonthDialog]);

  const updatePreviousMonthReflectionNotes = useCallback((value) => {
    setPreviousMonthDialog((currentDialog) => ({
      ...currentDialog,
      reflectionNotes: value,
    }));
  }, []);

  const openPreviousMonthDialog = useCallback(async (group, item) => {
    const previousMonthKpiDataId = activeDraft?.previousMonthKPIDataId;

    if (!previousMonthKpiDataId) {
      ToastService.warning("No previous month scorecard is available for this KPI yet.");
      return;
    }

    setPreviousMonthDialog({
      open: true,
      groupLabel: humanizeLabel(group.groupName),
      itemLabel: humanizeLabel(item.name),
      valueLabel: "",
      focus: null,
      reflection: null,
      reflectionNotes: "",
      loading: true,
      error: null,
      month: null,
      year: null,
      readOnly: isViewingHistory,
    });

    try {
      const { goals: previousGoals, scorecard: previousScorecard } =
        await service.loadPreviousMonthData(previousMonthKpiDataId);

      const previousGoal = findGoal(group.groupName, item.name, previousGoals);

      if (!previousGoal) {
        setPreviousMonthDialog((currentDialog) => ({
          ...currentDialog,
          loading: false,
          error: "No previous-month entry was stored for this KPI.",
        }));
        return;
      }

      const previousItem = findScorecardItem(previousScorecard, group.groupName, item.name);

      setPreviousMonthDialog({
        open: true,
        groupLabel: humanizeLabel(group.groupName),
        itemLabel: humanizeLabel(item.name),
        valueLabel: previousItem ? formatValue(previousItem.value, previousItem) : "No saved value",
        focus: previousGoal,
        reflection: previousGoal.reflection ?? null,
        reflectionNotes: previousGoal.reflection?.notes ?? "",
        loading: false,
        error: previousItem || previousGoal ? null : "No previous-month entry was stored for this KPI.",
        month: previousScorecard?.month ?? null,
        year: previousScorecard?.year ?? null,
        readOnly: isViewingHistory,
      });
    } catch (error) {
      logger.error("Error loading previous month KPI data:", error);
      setPreviousMonthDialog({
        open: true,
        groupLabel: humanizeLabel(group.groupName),
        itemLabel: humanizeLabel(item.name),
        valueLabel: "",
        focus: null,
        reflection: null,
        reflectionNotes: "",
        loading: false,
        error: "Unable to load the previous month entry for this KPI.",
        month: null,
        year: null,
        readOnly: isViewingHistory,
      });
    }
  }, [activeDraft, findGoal, isViewingHistory, service]);

  const savePreviousMonthReflection = useCallback(async () => {
    const previousGoalId = previousMonthDialog.focus?.id;

    if (!previousGoalId || previousMonthDialog.loading) {
      return;
    }

    setIsReflectionSaving(true);

    try {
      const payload = {
        id: previousMonthDialog.reflection?.id ?? 0,
        kpiGoalId: previousGoalId,
        notes: previousMonthDialog.reflectionNotes ?? "",
        dateCreated: previousMonthDialog.reflection?.dateCreated ?? null,
        dateModified: previousMonthDialog.reflection?.dateModified ?? null,
      };
      const savedReflection = await service.saveReflection(payload);

      setPreviousMonthDialog((currentDialog) => ({
        ...currentDialog,
        reflection: savedReflection,
        reflectionNotes: savedReflection?.notes ?? payload.notes ?? "",
      }));

      if (activeDraft?.previousMonthKPIDataId) {
        service.invalidateGoalsCache(activeDraft.previousMonthKPIDataId);
      }

      ToastService.success("Previous month reflection saved.");
    } catch {
      // Error already logged in service
    } finally {
      setIsReflectionSaving(false);
    }
  }, [activeDraft, previousMonthDialog.focus?.id, previousMonthDialog.loading, previousMonthDialog.reflection, previousMonthDialog.reflectionNotes, service]);

  return {
    previousMonthDialog,
    isReflectionSaving,
    resetPreviousMonthDialog,
    closePreviousMonthDialog,
    openPreviousMonthDialog,
    updatePreviousMonthReflectionNotes,
    savePreviousMonthReflection,
  };
}
